import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, buildODataUrl, Toast, StandardDataGrid, getODataRows, resolveODataTotalCount } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';

const API_BASE = '/api/productrestrictions';

const DOC_TYPE_OPTIONS = [
  { value: '', label: 'Hepsi' },
  { value: 1, label: 'Sipariş' },
];

const gridActionButton = {
  border: 'none',
  borderRadius: 6,
  color: '#fff',
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  height: 30,
  lineHeight: '30px',
  padding: '0 10px',
  minWidth: 74,
};

function unwrapLookupItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}

function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  return desc ? `${code} - ${desc}` : code;
}

const docTypeLabel = (value) => {
  if (value === null || value === undefined || value === '') return 'Hepsi';
  const found = DOC_TYPE_OPTIONS.find(o => o.value === value);
  return found ? found.label : String(value);
};

const emptyHeader = {
  id: '',
  textId: '',
  description: '',
  documentType: '',
  customerGroupId: '',
  customerSubGroupId: '',
  customerId: '',
  isActive: true,
};

export function ProductRestrictionsPageLegacy() {
  usePageMeta('Ürün Kısıtlama', 'Belge tipi ve müşteri kriterleri bazında ürünleri satışa kapatın.');
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyHeader);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  // detail (product) rows being edited
  const [products, setProducts] = useState([]);
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);

  // product picker state
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [pendingProductId, setPendingProductId] = useState('');

  // lookups for header criteria
  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerSubGroups, setCustomerSubGroups] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [sortField, setSortField] = useState('TextId');
  const [sortDir, setSortDir] = useState('asc');
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let filterParams = '';
      if (filterText.trim()) {
        const q = filterText.trim().replace(/'/g, "''").toLowerCase();
        filterParams = `(contains(tolower(TextId),'${q}') or contains(tolower(Description),'${q}'))`;
      }

      const url = buildODataUrl(API_BASE, { pageSize, currentPage, sortField, sortDir, filterParams });
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Veri alinamadi');

      const data = await res.json();
      const pageRows = getODataRows(data);
      setRows(pageRows);
      setTotalCount(await resolveODataTotalCount({
        data, rows: pageRows, currentPage, pageSize, base: API_BASE, filterParams, headers: authHeaders(),
      }));
    } catch (error) {
      showToast('error', error.message || 'Veri alinirken hata olustu');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterText, pageSize, sortDir, sortField]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // load lookups once
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/customergroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/customersubgroups/Select', { headers }).then(res => res.ok ? res.json() : []),
      fetch('/api/customers/Select', { headers }).then(res => res.ok ? res.json() : []),
    ]).then(([cg, csg, cust]) => {
      setCustomerGroups(unwrapLookupItems(cg));
      setCustomerSubGroups(unwrapLookupItems(csg));
      setCustomers(unwrapLookupItems(cust));
    }).catch(() => {});
  }, []);

  const handleFilterSearch = () => { setFilterText(filterInput); setCurrentPage(1); };
  const handleFilterKeyDown = (event) => { if (event.key === 'Enter') handleFilterSearch(); };
  const clearFilter = () => { setFilterText(''); setFilterInput(''); setCurrentPage(1); };

  const resetProductPicker = () => {
    setProductSearch('');
    setProductResults([]);
    setPendingProductId('');
  };

  const openNew = () => {
    setForm(emptyHeader);
    setProducts([]);
    setDeletedDetailIds([]);
    resetProductPicker();
    setSelected(null);
    setModal('new');
  };

  const loadDetails = useCallback(async (row, mode) => {
    if (!row) return;
    setSelected(row);
    setForm({
      id: row.id,
      textId: row.textId || '',
      description: row.description || '',
      documentType: row.documentType ?? '',
      customerGroupId: row.customerGroupId || '',
      customerSubGroupId: row.customerSubGroupId || '',
      customerId: row.customerId || '',
      isActive: row.isActive !== false,
    });
    setProducts([]);
    setDeletedDetailIds([]);
    resetProductPicker();
    setModal(mode);
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${row.id}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Detay alinamadi');
      const payload = body?.data ?? body?.Data ?? {};
      const detailList = (payload.details || []).map(d => ({
        id: d.id || d.Id,
        productId: d.productId ?? d.ProductId ?? null,
        label: d.label ?? d.Label ?? '',
      }));
      setProducts(detailList);
    } catch (error) {
      showToast('error', error.message || 'Detay alinirken hata olustu');
    }
  }, []);

  const openEdit = useCallback((row = selected) => loadDetails(row, 'edit'), [selected, loadDetails]);
  const openView = useCallback((row = selected) => loadDetails(row, 'view'), [selected, loadDetails]);

  const closeModal = () => { setModal(null); };

  const searchProducts = async () => {
    const q = productSearch.trim().replace(/'/g, "''").toLowerCase();
    if (!q) { setProductResults([]); return; }
    setSearchingProducts(true);
    try {
      const filter = `(contains(tolower(TextId),'${q}') or contains(tolower(Name),'${q}'))`;
      const url = `/api/products?$top=20&$orderby=TextId&$filter=${encodeURIComponent(filter)}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('Urun aramasi basarisiz');
      const data = await res.json();
      setProductResults(getODataRows(data));
    } catch (error) {
      showToast('error', error.message || 'Urun aranirken hata olustu');
    } finally {
      setSearchingProducts(false);
    }
  };

  const addProduct = () => {
    if (!pendingProductId) { showToast('error', 'Bir ürün seçiniz'); return; }
    if (products.some(p => p.productId === pendingProductId)) {
      showToast('error', 'Bu ürün zaten eklenmiş');
      return;
    }
    const picked = productResults.find(p => p.id === pendingProductId);
    const label = picked ? lookupLabel(picked) : '';
    setProducts(current => [...current, { id: '', productId: pendingProductId, label }]);
    resetProductPicker();
  };

  const removeProduct = (index) => {
    setProducts(current => {
      const target = current[index];
      if (target?.id) setDeletedDetailIds(ids => ids.includes(target.id) ? ids : [...ids, target.id]);
      return current.filter((_, i) => i !== index);
    });
  };

  const buildDetailPayload = () => products.map(p => ({
    id: p.id || '00000000-0000-0000-0000-000000000000',
    productId: p.productId,
  }));

  const handleSave = async () => {
    if (!form.textId.trim()) { showToast('error', 'Kod boş olamaz'); return; }
    try {
      const docType = form.documentType === '' || form.documentType === null ? null : Number(form.documentType);
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          header: {
            id: form.id || '00000000-0000-0000-0000-000000000000',
            textId: form.textId,
            description: form.description,
            documentType: docType,
            customerGroupId: form.customerGroupId || null,
            customerSubGroupId: form.customerSubGroupId || null,
            customerId: form.customerId || null,
            isActive: !!form.isActive,
          },
          details: buildDetailPayload(),
          deletedDetailIds,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt kaydedilemedi');
      showToast('success', 'Ürün kısıtlaması başarıyla kaydedildi');
      closeModal();
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ id: selected.id }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Ürün kısıtlaması başarıyla silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const customerGroupName = useMemo(() => {
    const map = new Map(customerGroups.map(g => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [customerGroups]);

  const customerSubGroupName = useMemo(() => {
    const map = new Map(customerSubGroups.map(g => [g.id, lookupLabel(g)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [customerSubGroups]);

  const customerName = useMemo(() => {
    const map = new Map(customers.map(c => [c.id, lookupLabel(c)]));
    return (id) => (id ? map.get(id) || '' : '');
  }, [customers]);

  const columns = useMemo(() => [
    { field: 'textId', headerName: 'Kod', flex: 1, minWidth: 130, sortable: true },
    { field: 'description', headerName: 'Açıklama', flex: 1.4, minWidth: 200, sortable: true },
    {
      field: 'documentType', headerName: 'Belge Tipi', width: 110, sortable: true,
      renderCell: (params) => docTypeLabel(params.row.documentType),
    },
    {
      field: 'criteria', headerName: 'Müşteri Kriter', flex: 1.6, minWidth: 240, sortable: false, filterable: false,
      renderCell: (params) => {
        const parts = [];
        const cg = customerGroupName(params.row.customerGroupId);
        if (cg) parts.push(`Grup: ${cg}`);
        const csg = customerSubGroupName(params.row.customerSubGroupId);
        if (csg) parts.push(`EkGrup: ${csg}`);
        const c = customerName(params.row.customerId);
        if (c) parts.push(`Müşteri: ${c}`);
        return parts.length === 0 ? 'Tüm müşteriler' : parts.join(' / ');
      },
    },
    {
      field: 'isActive', headerName: 'Durum', width: 90, sortable: true,
      renderCell: (params) => (
        <span style={{
          padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
          background: params.row.isActive ? '#dcfce7' : '#fee2e2',
          color: params.row.isActive ? '#166534' : '#991b1b',
        }}>{params.row.isActive ? 'Aktif' : 'Pasif'}</span>
      ),
    },
    {
      field: 'actions', headerName: 'İşlemler', width: 260, sortable: false, filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', height: '100%' }}>
          <button style={{ ...gridActionButton, background: 'var(--accent)' }} onClick={(event) => { event.stopPropagation(); openView(params.row); }}>Görüntüle</button>
          <button style={{ ...gridActionButton, background: 'var(--warn)' }} onClick={(event) => { event.stopPropagation(); openEdit(params.row); }}>Değiştir</button>
          <button
            style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48 }}
            onClick={(event) => { event.stopPropagation(); setSelected(params.row); setDeleteConfirm(true); }}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], [openEdit, openView, customerGroupName, customerSubGroupName, customerName]);

  const isView = modal === 'view';

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h2 style={s.pageTitle}>Ürün Kısıtlama</h2>
          <p style={s.pageSubtitle}>Belge tipi ve müşteri kriterleri bazında ürünleri satışa kapatın.</p>
        </div>
        <button style={{ ...s.btnPrimary, background: 'var(--success)' }} onClick={openNew}>+ Yeni Kısıtlama</button>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          <input
            style={s.filterInput}
            type="text"
            placeholder="Kod veya açıklama ile filtrele..."
            value={filterInput}
            onChange={e => setFilterInput(e.target.value)}
            onKeyDown={handleFilterKeyDown}
          />
          {filterText && <button style={s.btnSecondary} onClick={clearFilter}>Temizle</button>}
          <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
        </div>
      </div>

      <StandardDataGrid
        rows={rows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={page => setCurrentPage(page)}
        onPageSizeChange={size => { setPageSize(size); setCurrentPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, direction) => { setCurrentPage(1); setSortField(field || 'TextId'); setSortDir(direction || 'asc'); }}
        selectedId={selected?.id}
        onRowClick={row => setSelected(row)}
        onRowDoubleClick={row => openView(row)}
        exportFileName="urun-kisitlamalari"
      />

      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={{ ...s.modal, maxWidth: 760 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>
                {modal === 'new' ? 'Yeni Ürün Kısıtlaması' : modal === 'edit' ? 'Ürün Kısıtlaması Düzenle' : 'Ürün Kısıtlaması Detay'}
              </h3>
              <button style={s.closeBtn} onClick={closeModal}>x</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Kod *</label>
                  <input
                    style={isView ? s.inputView : s.input}
                    value={form.textId}
                    onChange={e => setForm(c => ({ ...c, textId: e.target.value }))}
                    readOnly={isView}
                    placeholder="Kod giriniz"
                    autoFocus
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Açıklama</label>
                  <input
                    style={isView ? s.inputView : s.input}
                    value={form.description}
                    onChange={e => setForm(c => ({ ...c, description: e.target.value }))}
                    readOnly={isView}
                    placeholder="Açıklama giriniz"
                  />
                </div>
                <div style={{ ...s.formGroup, maxWidth: 110 }}>
                  <label style={s.label}>Aktif</label>
                  <input
                    type="checkbox"
                    checked={!!form.isActive}
                    disabled={isView}
                    onChange={e => setForm(c => ({ ...c, isActive: e.target.checked }))}
                    style={{ width: 22, height: 22, marginTop: 6 }}
                  />
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Belge Tipi</label>
                  <select
                    style={isView ? s.inputView : s.select}
                    value={form.documentType === null ? '' : form.documentType}
                    disabled={isView}
                    onChange={e => setForm(c => ({ ...c, documentType: e.target.value === '' ? '' : Number(e.target.value) }))}
                  >
                    {DOC_TYPE_OPTIONS.map(opt => (
                      <option key={String(opt.value)} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Müşteri Grubu</label>
                  <select
                    style={isView ? s.inputView : s.select}
                    value={form.customerGroupId || ''}
                    disabled={isView}
                    onChange={e => setForm(c => ({ ...c, customerGroupId: e.target.value }))}
                  >
                    <option value="">Tümü</option>
                    {customerGroups.map(g => <option key={g.id} value={g.id}>{lookupLabel(g)}</option>)}
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Müşteri Ek Grubu</label>
                  <select
                    style={isView ? s.inputView : s.select}
                    value={form.customerSubGroupId || ''}
                    disabled={isView}
                    onChange={e => setForm(c => ({ ...c, customerSubGroupId: e.target.value }))}
                  >
                    <option value="">Tümü</option>
                    {customerSubGroups.map(g => <option key={g.id} value={g.id}>{lookupLabel(g)}</option>)}
                  </select>
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Müşteri</label>
                  <select
                    style={isView ? s.inputView : s.select}
                    value={form.customerId || ''}
                    disabled={isView}
                    onChange={e => setForm(c => ({ ...c, customerId: e.target.value }))}
                  >
                    <option value="">Tümü</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{lookupLabel(c)}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ borderTop: '1px solid var(--border-color, #e5e7eb)', paddingTop: 14, marginTop: 6 }}>
                <label style={{ ...s.label, fontSize: 14, marginBottom: 8, display: 'block' }}>Satışa Kapalı Ürünler</label>

                {!isView && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
                    <div style={{ ...s.formGroup, flex: 1, minWidth: 260 }}>
                      <label style={s.label}>Ürün (kod/ad ara)</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input
                          style={{ ...s.input, flex: 1 }}
                          value={productSearch}
                          onChange={e => setProductSearch(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchProducts(); } }}
                          placeholder="Ürün kodu veya adı"
                        />
                        <button style={s.btnSecondary} onClick={searchProducts} disabled={searchingProducts}>{searchingProducts ? '...' : 'Ara'}</button>
                      </div>
                      {productResults.length > 0 && (
                        <select style={{ ...s.select, marginTop: 6 }} value={pendingProductId} onChange={e => setPendingProductId(e.target.value)}>
                          <option value="">Sonuçlardan seçiniz</option>
                          {productResults.map(p => <option key={p.id} value={p.id}>{lookupLabel(p)}</option>)}
                        </select>
                      )}
                    </div>
                    <button style={{ ...s.btnPrimary, height: 38 }} onClick={addProduct}>Ekle</button>
                  </div>
                )}

                {products.length === 0 ? (
                  <p style={{ margin: '4px 0', color: 'var(--text-secondary, #6b7280)', fontSize: 13 }}>Henüz ürün eklenmedi.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {products.map((p, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10, background: 'var(--bg-card, #f9fafb)' }}>
                        <span style={{ fontSize: 13, color: 'var(--text-primary, #1f2937)' }}>{p.label || '(ürün)'}</span>
                        {!isView && (
                          <button style={{ ...gridActionButton, background: '#E53E3E', minWidth: 48, height: 26, lineHeight: '26px' }} onClick={() => removeProduct(index)}>Çıkar</button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={closeModal}>Kapat</button>
              {!isView && <button style={s.btnPrimary} onClick={handleSave}>Kaydet</button>}
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Kısıtlamayı Sil</h3>
              <button style={s.closeBtn} onClick={() => setDeleteConfirm(false)}>x</button>
            </div>
            <div style={s.modalBody}>
              <p>"{selected.textId}" kısıtlamasını silmek istediğinize emin misiniz? Bağlı tüm ürün satırları da silinecek.</p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>Vazgeç</button>
              <button style={{ ...s.btnPrimary, background: '#E53E3E' }} onClick={handleDelete}>Sil</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast type={toast.type} message={toast.message} />}
    </div>
  );
}
